import React, { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { Link, useHistory } from "react-router-dom";

import ROUTES from "manager/config/routes";
import {
  useActivateCosign,
  useConnectExistingCosignAccount,
  useCosignConnectInfo,
  useCosignDashboardAccess,
  useCreateCosignPropertyManager,
} from "manager/hooks/api";
import { Button } from "shared/components/Button";
import Container from "shared/components/Container";
import Icon from "shared/components/Icon";
import PageTitle from "shared/components/PageTitle";
import RadioGroupBoxedList from "shared/components/RadioGroupBoxedList";
import Skeleton from "shared/components/Skeleton";
import { NOTIFICATIONS } from "shared/config/constants";
import { usePermissions } from "shared/hooks";
import { openNotification } from "shared/utils/ui";

import { isValidCosignPropertyManagerUuid } from "./dashboard/cosignDashboard.config";
import { getPmUuidErrorStatusCode } from "./dashboard/useCosignPmUuidEdit";
import {
  CosignButtonSpinner,
  CosignConnectAlert,
  CosignConnectCard,
  CosignErrorIcon,
  CosignHelpText,
  CosignLogoWrapper,
  CosignSetupInput,
  CosignUuidError,
  CosignUuidInput,
  CosignUuidLabel,
  CosignUuidValue,
  CreatePropertyError,
} from "./styled";

enum ConnectMode {
  CREATE = "create",
  EXISTING = "existing",
}

const CONNECT_MODE_OPTIONS = [
  {
    id: ConnectMode.CREATE,
    title: "Create new Cosign account",
    subTitle: "Starts in Onboarding until Cosign activates it.",
  },
  {
    id: ConnectMode.EXISTING,
    title: "Connect existing Cosign account",
    subTitle:
      "Link an account that already exists in Cosign using its account UUID.",
  },
];

enum ConnectStatus {
  IDLE = "idle",
  VERIFYING = "verifying",
  INVALID = "invalid",
  UNREACHABLE = "unreachable",
  VERIFIED = "verified",
}

const VERIFIED_REDIRECT_DELAY_MS = 1500;

// A 5xx, or a request that got no response (the API client sets statusCode to
// ApiError.NETWORK_ERROR), means the UUID couldn't be checked, not that it's wrong.
const isConnectUnreachableError = (error: any) => {
  const statusCode = getPmUuidErrorStatusCode(error);
  return typeof statusCode !== "number" || statusCode >= 500;
};

const extractCosignErrorMessage = (error: any, fallback: string) =>
  error?.errors?.error ||
  error?.errors?.nonFieldErrors?.[0] ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const CosignConnect = () => {
  const history = useHistory();

  const { isIntegrationsAdmin } = usePermissions();

  const {
    cosignConnectInfo,
    isCosignConnectInfoLoading,
    cosignConnectInfoError,
  } = useCosignConnectInfo();
  const { activateCosign, isActivatingCosign } = useActivateCosign();
  const { canAccessCosignDashboard } = useCosignDashboardAccess();
  const {
    createCosignPropertyManager,
    isCreatingCosignPropertyManager,
  } = useCreateCosignPropertyManager();

  const { connectExistingCosignAccount, isConnectingExistingCosignAccount } =
    useConnectExistingCosignAccount();

  const [connectMode, setConnectMode] = useState(ConnectMode.CREATE);
  const [accountName, setAccountName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [existingUuid, setExistingUuid] = useState("");
  const [connectStatus, setConnectStatus] = useState(ConnectStatus.IDLE);

  // The connect mutation refreshes connect-info before its per-call onSuccess
  // runs, so keep the existing-account form (and its Verified banner) on screen
  // from submit until the banner's own redirect.
  const isConnectingExisting =
    connectStatus === ConnectStatus.VERIFYING ||
    connectStatus === ConnectStatus.VERIFIED;

  // Activated integrations admins use the mapping dashboard; others stay on connect.
  useEffect(() => {
    if (canAccessCosignDashboard && !isConnectingExisting) {
      history.replace(ROUTES.cosignDashboard);
    }
  }, [canAccessCosignDashboard, isConnectingExisting, history]);

  useEffect(() => {
    if (connectStatus !== ConnectStatus.VERIFIED) {
      return undefined;
    }
    const timer = setTimeout(
      () => history.replace(ROUTES.cosignDashboard),
      VERIFIED_REDIRECT_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [connectStatus, history]);

  const onActivateSuccess = () => history.replace(ROUTES.cosignDashboard);

  const onActivate = () => {
    activateCosign(undefined, {
      onSuccess: () => {
        onActivateSuccess();
      },
      onError: (error: any) => {
        // The axios interceptor wraps failures in ApiError, so the backend's
        // message lands on error.errors (camelCased payload), not error.response.
        // 4xx/5xx with { error } -> errors.error; 403 with { detail } -> nonFieldErrors.
        const message =
          error?.errors?.error ||
          error?.errors?.nonFieldErrors?.[0] ||
          "Unable to verify the Cosign account. Please try again.";
        openNotification(message, NOTIFICATIONS.error);
      },
    });
  };

  // Path B (PRODUCT-987): new customer with no Property Manager yet. VERO creates
  // the PM in Cosign, persists the returned UUID, and enables Cosign. The PM
  // starts in Onboarding, so we route to the dashboard where that status banner
  // is surfaced. On failure nothing is persisted and an error is shown.
  const onCreatePropertyManager = () => {
    setCreateError(null);
    createCosignPropertyManager(accountName.trim() || undefined, {
      onSuccess: () => {
        openNotification(
          "Cosign account created. It's in Onboarding — Cosign must activate it before referrals are possible.",
          NOTIFICATIONS.info
        );
        history.replace(ROUTES.cosignDashboard);
      },
      onError: (error: any) => {
        setCreateError(
          extractCosignErrorMessage(
            error,
            "Unable to create the Cosign account. Please try again."
          )
        );
      },
    });
  };

  // PRODUCT-2233: attach a PM that already exists in Cosign, e.g. one shared
  // with the client's other VERO companies. The backend verifies it with Cosign
  // first and saves nothing on failure.
  const onConnectExisting = () => {
    const uuid = existingUuid.trim();
    if (!isValidCosignPropertyManagerUuid(uuid)) {
      setConnectStatus(ConnectStatus.INVALID);
      return;
    }

    setConnectStatus(ConnectStatus.VERIFYING);
    connectExistingCosignAccount(uuid, {
      onSuccess: () => setConnectStatus(ConnectStatus.VERIFIED),
      onError: (error: any) =>
        setConnectStatus(
          isConnectUnreachableError(error)
            ? ConnectStatus.UNREACHABLE
            : ConnectStatus.INVALID
        ),
    });
  };

  const onChangeConnectMode = (mode: ConnectMode) => {
    setConnectMode(mode);
    if (mode === ConnectMode.CREATE) {
      setConnectStatus(ConnectStatus.IDLE);
    }
  };

  const getConnectExistingButtonLabel = () => {
    if (isConnectingExistingCosignAccount) {
      return "Verifying…";
    }
    return connectStatus === ConnectStatus.UNREACHABLE
      ? "Try again"
      : "Verify & connect";
  };

  return (
    <Container noPaddingTop>
      <Helmet>
        <title>Connect Cosign</title>
      </Helmet>

      <PageTitle.Container>
        <PageTitle>Connect Cosign</PageTitle>
      </PageTitle.Container>

      <Skeleton loading={isCosignConnectInfoLoading}>
        <CosignConnectCard>
          <CosignLogoWrapper>
            <Icon.CosignPersonIcon width={140} height={32} />
          </CosignLogoWrapper>

          {cosignConnectInfoError ? (
            <p data-testid="cosign-connect-error">
              We couldn&apos;t load the Cosign connection details. Please refresh
              the page or try again later.
            </p>
          ) : cosignConnectInfo?.hasUuid && !isConnectingExisting ? (
            <>
              <CosignUuidLabel>Cosign account ID</CosignUuidLabel>
              <CosignUuidValue data-testid="cosign-masked-uuid">
                {cosignConnectInfo.maskedUuid}
              </CosignUuidValue>
              {cosignConnectInfo.isConnected ? (
                <p data-testid="cosign-already-connected">
                  This Cosign account is connected. The account status is shown
                  on the marketplace tile.
                </p>
              ) : isIntegrationsAdmin ? (
                <>
                  <p>
                    Confirm this Cosign account to activate the integration. The
                    account status will be shown on the dashboard.
                  </p>
                  <Button
                    onClick={onActivate}
                    loading={isActivatingCosign}
                    disabled={isActivatingCosign}
                    data-testid="cosign-activate-button"
                  >
                    Activate Cosign
                  </Button>
                </>
              ) : (
                <p data-testid="cosign-activate-no-permission">
                  This Cosign account is ready to activate. Please ask an
                  integrations admin to complete the activation.
                </p>
              )}
            </>
          ) : (
            <div data-testid="cosign-no-account">
              {isIntegrationsAdmin ? (
                <>
                  <p>
                    No Cosign account is set up for this company yet. Create a
                    new one, or connect an account this company already has in
                    Cosign.
                  </p>
                  <RadioGroupBoxedList
                    listTitle="How do you want to connect?"
                    listItems={CONNECT_MODE_OPTIONS}
                    currentSelection={connectMode}
                    onChangeSelection={onChangeConnectMode}
                    disabled={isConnectingExisting}
                  />
                  {connectMode === ConnectMode.CREATE ? (
                    <>
                      <CosignUuidLabel>Cosign account name</CosignUuidLabel>
                      <CosignSetupInput
                        data-testid="cosign-account-name-input"
                        value={accountName}
                        onChange={(event) => {
                          setAccountName(event.target.value);
                          setCreateError(null);
                        }}
                        placeholder="Defaults to your company name"
                        disabled={isCreatingCosignPropertyManager}
                      />
                      {createError && (
                        <CreatePropertyError data-testid="cosign-create-pm-error">
                          {createError}
                        </CreatePropertyError>
                      )}
                      <Button
                        onClick={onCreatePropertyManager}
                        loading={isCreatingCosignPropertyManager}
                        disabled={isCreatingCosignPropertyManager}
                        data-testid="cosign-create-pm-button"
                      >
                        Create Cosign account
                      </Button>
                    </>
                  ) : (
                    <>
                      <CosignUuidLabel>Cosign account UUID</CosignUuidLabel>
                      <CosignUuidInput
                        data-testid="cosign-existing-uuid-input"
                        value={existingUuid}
                        onChange={(event) => {
                          setExistingUuid(event.target.value);
                          setConnectStatus(ConnectStatus.IDLE);
                        }}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        spellCheck={false}
                        autoComplete="off"
                        disabled={isConnectingExisting}
                        aria-invalid={connectStatus === ConnectStatus.INVALID}
                        $invalid={connectStatus === ConnectStatus.INVALID}
                      />
                      {connectStatus === ConnectStatus.INVALID ? (
                        <CosignUuidError data-testid="cosign-connect-existing-error">
                          <CosignErrorIcon aria-hidden="true" />
                          <span>
                            No Cosign account matches this UUID. Check it and
                            try again. Nothing was saved.
                          </span>
                        </CosignUuidError>
                      ) : (
                        <CosignHelpText>
                          VERO checks this UUID with Cosign before saving.
                          Nothing is saved unless it&apos;s verified.
                        </CosignHelpText>
                      )}
                      {connectStatus === ConnectStatus.UNREACHABLE && (
                        <CosignConnectAlert
                          type="error"
                          showIcon
                          message="Couldn't reach Cosign"
                          description="We couldn't verify this UUID because Cosign didn't respond. Nothing was saved. Try again in a few minutes."
                          data-testid="cosign-connect-existing-unreachable"
                        />
                      )}
                      {connectStatus === ConnectStatus.VERIFIED && (
                        <CosignConnectAlert
                          type="success"
                          showIcon
                          message={
                            <>
                              Verified. Taking you to property mapping…{" "}
                              <Link to={ROUTES.cosignDashboard} replace>
                                Go now
                              </Link>
                            </>
                          }
                          data-testid="cosign-connect-existing-verified"
                        />
                      )}
                      {/* No `loading` prop: the shared Button hides its label while
                          loading, and the design shows "Verifying…" beside a spinner. */}
                      <Button
                        onClick={onConnectExisting}
                        PrefixIcon={
                          isConnectingExistingCosignAccount
                            ? CosignButtonSpinner
                            : undefined
                        }
                        disabled={isConnectingExisting || !existingUuid.trim()}
                        data-testid="cosign-connect-existing-button"
                      >
                        {getConnectExistingButtonLabel()}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <p>
                  No Cosign account is set up for this company yet. Please ask an
                  integrations admin to create the company&apos;s Cosign account.
                </p>
              )}
            </div>
          )}
        </CosignConnectCard>
      </Skeleton>
    </Container>
  );
};

export default CosignConnect;
